import * as d3 from 'd3-format'
import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Bar} from 'react-chartjs-2'
import {Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip} from 'chart.js'
import useChartTooltip from '../../../helpers/useChartTooltip'
import ChartBlankError from '../../../common/ChartBlankError'
import ChartDataRefetchOnError from '../../../common/ChartDataRefetchOnError'
import CircularProgress from '@mui/material/CircularProgress'
import ChartSimpleTooltip from '../../../common/ChartSimpleTooltip'
import queryPopulationDistribution from '../../../../api/queries/queryPopulationDistribution.js'
import {buildChartJsData, getPopulationDistributionOptions} from '../PopulationDistribution/helpers'
import {useStatesAndHealthConditions} from '../../../../store/hooks/breadCrumbs'
import {getDefaultState} from '../../../../shared/helpers/getState'
import loadingTitles from '../../../../appConfig/loadingTitles.config.json'
import {useTableViewContext} from '../../../../store/hooks/tableView'
import tableData from '../../../../appConfig/populationDistributionTable.config.json'
import TableVirtualized from '../../../TableVirtual/TableVirtualized'
import {useConfig} from '../../../../store/hooks/config'
import {getIncomeValue} from '../../../TableVirtual/SortHelper'
import theme from '../../../../themes/pTheme/pTheme'
import ToggleButtonGroup from '../../../common/ToggleButtonGroup/ToggleButtonGroup.js'
import {Box, Typography} from '@mui/material'
import {ChartWrapper, StyledDiv} from './PopulationDistributionStyles.js'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip)

export const colors = [theme.palette.hpPrimaryMain, theme.palette.hpPrimaryDark]

const PopulationDistributionChart = ({width, height, dimension}) => {
  const {loading, error, data, refetch, conditionalDistributionQuery} = queryPopulationDistribution(dimension)
  const {filters} = useStatesAndHealthConditions() || {}
  const ref = useRef()
  const {externalTooltipHandler, tooltipData, tooltipPosition, updateTooltipSize} = useChartTooltip()
  const defaultState = getDefaultState(filters) || []

  const [updatedColor, setUpdatedColor] = useState(colors)
  const [selectedYAxisView, setSelectedYAxisView] = useState('count')
  const {showPopulationDistributionTable} = useTableViewContext()
  const {statesConfig} = useConfig() || {}

  const toggleBtnDetails = [
    {
      tooltipProps: {title: 'Count'},
      toggleBtnProps: {value: 'count', 'aria-label': 'count', text: <Typography variant='body2'>Count</Typography>}
    },
    {
      tooltipProps: {title: 'Percentage'},
      toggleBtnProps: {
        value: 'percentage',
        'aria-label': 'percentage',
        text: <Typography variant='body2'>Percentage</Typography>
      }
    }
  ]
  useEffect(() => {
    if (typeof conditionalDistributionQuery === 'function') {
      conditionalDistributionQuery()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (data?.populationDistribution) {
      const uniqueStates = [...new Set(data.populationDistribution.map((obj) => obj.state))]
      if (uniqueStates.length < defaultState.length) {
        const index = defaultState.indexOf(uniqueStates[0])
        index > 0 ? setUpdatedColor([colors[1]]) : setUpdatedColor([colors[0]])
      } else if (defaultState.length === 2) {
        setUpdatedColor(colors)
      } else {
        setUpdatedColor([colors[0]])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const refetchChartData = (event) => {
    event.preventDefault()
    refetch()
  }

  const getTableColumnLabel = (key) => {
    switch (key) {
      case 'Dimension':
        return dimension
      case 'Selected Population %':
        return defaultState[0] === statesConfig?.entireUnitedStateLabel
          ? 'Selected Population \n (% of US)'
          : 'Selected Population \n (% of State)'
      default:
        return key
    }
  }

  const [tableColumns, setTableColumns] = React.useState(tableData)
  React.useMemo(() => {
    const dynamicColumn = Object.keys(tableData).map((obj) => ({
      label: getTableColumnLabel(obj),
      dataKey: tableData[obj],
      type: tableData[obj] === 'percentage' ? 'percentage' : null
    }))
    setTableColumns(dynamicColumn)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimension, defaultState])

  const normalizePercentage = (percentage) => {
    if (percentage.startsWith('<')) return '0.9'
    if (percentage.startsWith('>')) return '99.1'
    return percentage
  }

  const editLabel = () => {
    if (data) {
      for (const e of data.populationDistribution) {
        if (e.binName && e.binName.length > 0 && e.binName.includes('$')) {
          e.binNameUpdated = formatCurrencyLabel(e.binName)
          e.incomeSortValue = getIncomeValue(e.binName)
        } else {
          e.binNameUpdated = dimension === 'Age' ? Number.parseInt(e.binName) : e.binName
        }
        e.state =
          defaultState[0] === statesConfig?.entireUnitedStateLabel ? statesConfig?.entireUnitedStateLabel : e.state
        e.percentageNormalized = normalizePercentage(e.percentage)
      }
    }
  }

  const formatCurrencyLabel = (value) => {
    const start = value.includes('$') ? value.indexOf('$') : 0
    const end = value.includes('+') ? value.indexOf('+') : value.length
    let valArr = value.substring(start, end).replaceAll('+', '').replaceAll('$', '').trim().split('-')
    valArr = valArr.map((e) => {
      const val = e.trim().replaceAll(',', '')
      return d3.format('$.2s')(val)
    })

    let formattedValue = ''
    if (valArr.length > 1) {
      formattedValue = valArr[0] + '-' + valArr[1]
    } else {
      formattedValue = valArr[0]
    }

    return value.substring(0, start) + formattedValue + value.substring(end)
  }

  if (!loading && error) {
    return <ChartDataRefetchOnError onTryAgain={(event) => refetchChartData(event)} />
  }

  const conditionalRender = () => {
    if (data && (!data.populationDistribution || data.populationDistribution.length === 0)) {
      return <ChartBlankError chart={'PopulationDistribution'} boxheight={height} />
    } else if (
      data &&
      (data.populationDistribution || data.populationDistribution.length > 0) &&
      showPopulationDistributionTable
    ) {
      editLabel()
      return (
        <TableVirtualized
          height={height}
          width={width}
          columns={tableColumns}
          data={data.populationDistribution}
          dataLoading={loading}
          enablePadding={false}
          chartType='distribution'
        />
      )
    } else if (data?.populationDistribution) {
      editLabel()

      const chartJsData = buildChartJsData(data.populationDistribution, updatedColor, dimension)

      if (selectedYAxisView === 'percentage') {
        chartJsData.datasets = chartJsData.datasets.map((ds) => ({
          ...ds,
          data: ds._raw.map((e) => (e ? Number(e.percentageNormalized) : 0))
        }))
      }

      const options = getPopulationDistributionOptions(selectedYAxisView)
      options.plugins.tooltip = {
        ...options.plugins.tooltip,
        enabled: false,
        external: externalTooltipHandler
      }

      return (
        <>
          <Box mx={2} mb={1}>
            <ToggleButtonGroup
              isTextToggleButton={true}
              toggleButtons={toggleBtnDetails}
              onSelection={setSelectedYAxisView}
              defaultValue={selectedYAxisView}
            />
          </Box>
          <ChartWrapper chartWidth={width - 20} chartHeight={height - 20}>
            <Bar data={chartJsData} options={options} data-testid='chartjs-bar' />
          </ChartWrapper>
        </>
      )
    }
  }

  return (
    <div>
      {loading && (
        <StyledDiv>
          <CircularProgress title={loadingTitles.populationDistributionChart} />
        </StyledDiv>
      )}
      {
        <ChartSimpleTooltip
          ref={(node) => {
            ref.current = node
            updateTooltipSize(node)
          }}
          tooltipId='pop-distribution-tooltip'
          data={tooltipData}
          dimension={dimension}
          position={tooltipPosition}
          height={height}
        />
      }
      {!loading && conditionalRender()}
    </div>
  )
}

PopulationDistributionChart.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  dimension: PropTypes.string
}

export default PopulationDistributionChart
